import React from "react";
import styled from "styled-components";

import {
  fromAWSDate,
  weekdays,
} from "@examspark/common/dist/utils/coursePlanner";
import { Breadcrumbs } from "@examspark/common-app";
import {
  Button,
  Callout,
  CheckboxGridInput,
  DateComboInput,
  Label,
  RouterLink,
  SelectInput,
  Text,
  TextInput,
  Toolbar,
  VSpace,
} from "@examspark/common-ui";
import { LinkButton } from "@examspark/common-ui/dist/components/buttons/Button";
import {
  IconArchiveLine,
  IconCautionSolid,
  IconCopyLine,
  IconFailureSolid,
  IconTrashLine,
} from "@examspark/common-ui/dist/components/icons";
import { awsDateToDatePickerValue } from "@examspark/common-ui/dist/components/inputs/DateInput";
import { LabelLike } from "@examspark/common-ui/dist/components/inputs/Label";
import { useArrayReducer } from "@examspark/common-ui/dist/hooks/useArray";
import { unwrapSetStateAction } from "@examspark/common-ui/dist/utils/react";
import {
  GradingPeriod,
  NonInstructionDate,
  Replace,
  ResourceVisibilityType,
  Weekday,
} from "@examspark/types";

import PageHead from "../../common/PageHead";
import PageMenu from "../../common/PageMenu";
import PageTitle from "../../common/PageTitle";

import { NonInstructionDateTable } from "./NonInstructionDateTable";

const StyledGradingPeriods = styled.div`
  th {
    font-weight: normal;
    font-size: 1.1rem;
    text-align: left;
    color: #555;
  }
`;

const StyledWorkspaceForm = styled.div`
  flex: 0;
  flex-basis: 60rem;

  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

// the 1-parameter arm of `typeof useState<S>` (the actual useState,
//when called with 0 params, forces S to also allow undefined)
type useStateReturnType<S> = [S, React.Dispatch<React.SetStateAction<S>>];

export type HolidayWithId = NonInstructionDate & { id: string } & {
  isDisabled?: boolean;
};
export type GradingPeriodWithId = Replace<
  Partial<GradingPeriod>,
  {
    startAt?: GradingPeriod["startAt"] | null;
    endAt?: GradingPeriod["endAt"] | null;
  }
> & { id: string };
export type WorkspaceCalendarProps = {
  action: "Create" | "Edit";

  name: useStateReturnType<string>;
  startAt: useStateReturnType<string | null>;
  endAt: useStateReturnType<string | null>;
  visibility: useStateReturnType<ResourceVisibilityType | null>;
  isDefault?: boolean;

  instructionWeekdays: useStateReturnType<Weekday[]>;

  holidays: ReturnType<typeof useArrayReducer<HolidayWithId>>;

  gradingPeriodLength: useStateReturnType<string | null>;
  gradingPeriods: ReturnType<typeof useArrayReducer<GradingPeriodWithId>>;

  onSave: () => void;
  onArchive?: () => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  onSetAsDefault?: () => void;
};

export const CalendarForm = ({
  action,
  name: [name, setName],
  startAt: [startAt, setStartAt],
  endAt: [endAt, setEndAt],
  isDefault,
  instructionWeekdays: [instructionWeekdays, setInstructionWeekdays],
  holidays: {
    values: holidays,
    onCreate: onAddHoliday,
    onDelete: onRemoveHoliday,
    onUpdate: onHolidayChange,
  },
  visibility: [visibility, setVisibility],
  gradingPeriodLength: [gradingPeriodLength, setGradingPeriodLength],
  gradingPeriods: { values: gradingPeriods, onUpdate: onGradingPeriodChange },

  onArchive,
  onDelete,
  onDuplicate,
  onSave,
  onSetAsDefault,
}: WorkspaceCalendarProps) => {
  let daysBetweenStartAndEnd = 0;
  if (startAt && endAt) {
    daysBetweenStartAndEnd =
      (+fromAWSDate(endAt) - +fromAWSDate(startAt)) / 1000 / 60 / 60 / 24;
  }

  // filter out 'unused' grading periods based on the length
  const actualGradingPeriods = gradingPeriods.filter(
    (_, idx) => idx < (gradingPeriodLength === "6_WEEKS" ? 6 : 4),
  );
  const gradingPeriodsExist = actualGradingPeriods.every(
    (gp) => gp.startAt && gp.endAt,
  );
  // note: We are comparing date strings with < but that's okay because
  // AWDates are sortable
  const gradingPeriodsAreOrdered = actualGradingPeriods.every(
    (gp) => gp.startAt && gp.endAt && gp.startAt <= gp.endAt,
  );

  const canSave =
    startAt &&
    endAt &&
    startAt < endAt &&
    daysBetweenStartAndEnd < 365 * 2 &&
    gradingPeriodsAreOrdered;

  return (
    <VSpace size="3rem">
      <PageHead>
        <Breadcrumbs
          items={[
            ...Breadcrumbs.Dashboard(),
            <RouterLink to={"/workspace"} key="workspace">
              Workspace
            </RouterLink>,
            <RouterLink to={"/workspace/calendars"} key="workspace/calendars">
              Calendars
            </RouterLink>,
            <RouterLink to={"/calendar"} key="calendar">
              Manage
            </RouterLink>,
          ]}
        />
        <Toolbar>
          <PageTitle>{action} Academic Calendar</PageTitle>
          <Toolbar.Right>
            <PageMenu
              disabled={false}
              buttons={[
                onDuplicate && {
                  title: "Duplicate",
                  icon: <IconCopyLine />,
                  onClick: onDuplicate,
                  // allow cloning of shared calendars
                  disabled: false,
                },
                onArchive && {
                  title: "Archive",
                  icon: <IconArchiveLine />,
                  onClick: onArchive,
                },
                onDelete && {
                  title: "Delete",
                  icon: <IconTrashLine />,
                  onClick: onDelete,
                },
              ]}
            />
          </Toolbar.Right>
        </Toolbar>
      </PageHead>
      <StyledWorkspaceForm>
        {!!isDefault && (
          <Callout icon={<IconCautionSolid fill="orange" width="16" />}>
            This is the default calendar for your workspace.
          </Callout>
        )}
        <Label label="Calendar Name">
          <TextInput value={name} onChange={(e) => setName(e.target.value)} />
        </Label>

        {/* 
            I am purposefully leaving out Private for now until we have a
            Creator field or something similar.
          */}
        <Label label="Visibility">
          <SelectInput.Simple
            options={[
              {
                value: ResourceVisibilityType.Workspace,
                label: "Your Workspace",
              },
              {
                value: ResourceVisibilityType.WorkspaceShareable,
                label: "Your Workspace + Workspaces You Share With",
              },
            ]}
            value={visibility}
            onChange={(value) => {
              // silly type check
              if (
                value === ResourceVisibilityType.WorkspaceShareable ||
                value === ResourceVisibilityType.Workspace
              ) {
                setVisibility(value);
              }
            }}
          />
        </Label>

        {daysBetweenStartAndEnd < 0 && (
          <>
            <Callout icon={<IconFailureSolid fill="red" width="16" />}>
              Start date must be before end date.
            </Callout>
          </>
        )}
        {daysBetweenStartAndEnd > 365 && daysBetweenStartAndEnd < 365 * 2 && (
          <>
            <Callout icon={<IconCautionSolid fill="orange" width="16" />}>
              Academic calendar is longer than 12 months. Are you sure?
            </Callout>
          </>
        )}
        {daysBetweenStartAndEnd > 365 * 2 && (
          <>
            <Callout icon={<IconFailureSolid fill="red" width="16" />}>
              Academic calendars cannot be longer than 24 months.
            </Callout>
          </>
        )}

        <Toolbar>
          <Label label="Start/End Date">
            <DateComboInput
              start={startAt}
              end={endAt}
              // @ts-expect-error think these types are for older calendar
              minDetail="month"
              onChange={(value) => {
                setStartAt(value?.[0] ?? null);
                setEndAt(value?.[1] ?? null);
              }}
            />
          </Label>
        </Toolbar>

        <NonInstructionDateTable
          holidays={holidays}
          onAddHoliday={onAddHoliday}
          onRemoveHoliday={onRemoveHoliday}
          onHolidayChange={onHolidayChange}
        />

        <LabelLike label="Instructional Days of Week">
          <CheckboxGridInput
            value={instructionWeekdays}
            onChange={setInstructionWeekdays}
          >
            {weekdays.map((weekday) => (
              <CheckboxGridInput.Item key={weekday} value={weekday}>
                {weekday}
              </CheckboxGridInput.Item>
            ))}
          </CheckboxGridInput>
        </LabelLike>

        <Label label="Grading Period Interval">
          <SelectInput.Simple
            options={[
              { value: "6_WEEKS", label: "6 Weeks" },
              { value: "9_WEEKS", label: "9 Weeks" },
            ]}
            value={gradingPeriodLength}
            onChange={setGradingPeriodLength}
          />
        </Label>
        <StyledGradingPeriods>
          <VSpace>
            <Text.H3>Grading Periods</Text.H3>
            {gradingPeriodsExist ? (
              <>
                {!gradingPeriodsAreOrdered && (
                  <>
                    <Callout icon={<IconFailureSolid fill="red" width="16" />}>
                      Grading Period start date must be before end date.
                    </Callout>
                  </>
                )}
              </>
            ) : (
              <>
                <Callout icon={<IconFailureSolid fill="red" width="16" />}>
                  Grading Period start and end dates cannot be blank.
                </Callout>
              </>
            )}
            <table style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th style={{ width: "100%" }}>Label</th>
                  <th>Start/End Date</th>
                </tr>
              </thead>
              <tbody>
                {gradingPeriods
                  .filter((_, idx) => {
                    // only show as many grading periods as request by length
                    // (we keep the state array at 6 always so that the user doesn't
                    // lose their data if they change the length)
                    return idx < (gradingPeriodLength === "6_WEEKS" ? 6 : 4);
                  })
                  .map((gradingPeriod, idx) => {
                    const prevEnd =
                      idx > 0 ? gradingPeriods[idx - 1]?.endAt : startAt;

                    return (
                      <tr key={gradingPeriod.id}>
                        <td>
                          <TextInput
                            fullWidth
                            value={gradingPeriod.label ?? ""}
                            onChange={(e) => {
                              onGradingPeriodChange(gradingPeriod.id, {
                                label: e.target.value,
                              });
                            }}
                          />
                        </td>
                        <td>
                          <DateComboInput
                            start={gradingPeriod.startAt ?? null}
                            end={gradingPeriod.endAt ?? null}
                            minDate={
                              awsDateToDatePickerValue(startAt) ?? undefined
                            }
                            maxDate={
                              awsDateToDatePickerValue(endAt) ?? undefined
                            }
                            // @ts-expect-error think these types are for older calendar
                            minDetail="month"
                            defaultActiveStartDate={
                              prevEnd
                                ? new Date(
                                    +new Date(fromAWSDate(prevEnd)) +
                                      86400 * 1000 * 2,
                                  )
                                : undefined
                            }
                            onChange={(value) => {
                              onGradingPeriodChange(gradingPeriod.id, {
                                startAt: unwrapSetStateAction(
                                  gradingPeriod.startAt,
                                  value?.[0] ?? null,
                                ),
                                endAt: unwrapSetStateAction(
                                  gradingPeriod.endAt,
                                  value?.[1] ?? null,
                                ),
                              });
                            }}
                          />
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </VSpace>
        </StyledGradingPeriods>

        <Toolbar>
          <Toolbar.Right>
            <LinkButton to="/calendar">Cancel</LinkButton>
            {onSetAsDefault && (
              <Button
                title="Set as Default for Workspace"
                onClick={onSetAsDefault}
              >
                Save as Default
              </Button>
            )}
            <Button
              color="primary"
              title="Save Calendar"
              onClick={onSave}
              disabled={canSave ? undefined : true}
            >
              Save Calendar
            </Button>
          </Toolbar.Right>
        </Toolbar>
      </StyledWorkspaceForm>
    </VSpace>
  );
};
